import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import DirectoryRowCard from "../components/DirectoryRowCard";
import {
  saasCircleCountSx,
  saasDirectoryWrapSx,
  saasHeroActionButtonSx,
  saasInputSx,
  saasSectionCardSx,
  saasSoftCountSx,
} from "../components/saasStyles";

const initialAppointmentForm = {
  technicianId: "",
  optionAStart: "",
  optionAEnd: "",
  optionBStart: "",
  optionBEnd: "",
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toInputDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hour = `${date.getHours()}`.padStart(2, "0");
  const minute = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

export default function PmoPage() {
  const [queue, setQueue] = useState([]);
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedTechnicianId, setSelectedTechnicianId] = useState("");
  const [selectedQueueId, setSelectedQueueId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState(initialAppointmentForm);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [quickAction, setQuickAction] = useState("default");

  const loadData = async () => {
    const [queueResponse, techniciansResponse, ordersResponse, calendarResponse] = await Promise.all([
      api.get("/api/pmo/queue"),
      api.get("/api/technicians"),
      api.get("/api/orders"),
      api.get("/api/pmo/calendar"),
    ]);
    setQueue(queueResponse.data);
    setTechnicians(techniciansResponse.data);
    setCalendarEntries(calendarResponse.data);
    setOrders(
      ordersResponse.data.filter((order) =>
        ["ready_for_delivery", "pmo_in_charge", "technician_assigned", "appointment_proposed", "appointment_confirmed"].includes(order.status)
      )
    );
  };

  const loadOrderDetail = async (id) => {
    const response = await api.get(`/api/orders/${id}`);
    setSelectedOrder(response.data);
  };

  useEffect(() => {
    loadData().catch(() => setMessage({ type: "error", text: "Les donnees PMO n'ont pas pu etre chargees." }));
  }, []);

  useEffect(() => {
    if (!selectedOrderId) {
      setSelectedOrder(null);
      setAppointmentForm(initialAppointmentForm);
      return;
    }
    loadOrderDetail(selectedOrderId).catch(() => setMessage({ type: "error", text: "Les details PMO n'ont pas pu etre charges." }));
  }, [selectedOrderId]);

  useEffect(() => {
    const selectedEntry = queue.find((entry) => String(entry.id) === String(selectedOrderId));
    if (!selectedEntry) {
      return;
    }

    setAppointmentForm({
      technicianId: selectedEntry.appointment_technician_id ? String(selectedEntry.appointment_technician_id) : selectedTechnicianId || "",
      optionAStart: toInputDateTime(selectedEntry.option_a_start),
      optionAEnd: toInputDateTime(selectedEntry.option_a_end),
      optionBStart: toInputDateTime(selectedEntry.option_b_start),
      optionBEnd: toInputDateTime(selectedEntry.option_b_end),
    });
  }, [queue, selectedOrderId, selectedTechnicianId]);

  const filteredQueue = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = !needle
      ? queue
      : queue.filter((entry) =>
          [entry.contact_name, entry.whatsapp_number, entry.location_text, entry.status, entry.technician_name]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle)
        );

    if (quickAction === "ready") return base.filter((entry) => entry.status === "ready_for_delivery");
    if (quickAction === "assigned") return base.filter((entry) => entry.technician_name);
    if (quickAction === "confirmed") return base.filter((entry) => entry.confirmed_start);
    return base;
  }, [queue, search, quickAction]);

  const handleAssign = async () => {
    try {
      await api.post(`/api/orders/${selectedOrderId}/assign-technician`, {
        technicianId: Number(selectedTechnicianId),
      });
      await loadData();
      await loadOrderDetail(selectedOrderId);
      setMessage({ type: "success", text: "Le technicien a ete affecte." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le technicien n'a pas pu etre affecte." });
    }
  };

  const handleAppointment = async () => {
    try {
      await api.post(`/api/orders/${selectedOrderId}/appointments`, {
        technicianId: appointmentForm.technicianId ? Number(appointmentForm.technicianId) : null,
        optionAStart: appointmentForm.optionAStart,
        optionAEnd: appointmentForm.optionAEnd,
        optionBStart: appointmentForm.optionBStart,
        optionBEnd: appointmentForm.optionBEnd,
      });
      setAppointmentForm(initialAppointmentForm);
      await loadData();
      await loadOrderDetail(selectedOrderId);
      setMessage({ type: "success", text: "Les propositions de rendez-vous ont ete enregistrees." });
    } catch (_error) {
      setMessage({ type: "error", text: "Les propositions de rendez-vous n'ont pas pu etre enregistrees." });
    }
  };

  const handleConfirm = async (confirmedStart) => {
    if (!selectedOrder?.history?.length) {
      return;
    }

    const queueEntry = queue.find((entry) => String(entry.id) === String(selectedOrderId));
    if (!queueEntry?.appointment_id || !confirmedStart) {
      return;
    }

    try {
      await api.post(`/api/appointments/${queueEntry.appointment_id}/confirm`, {
        confirmedStart,
        confirmedEnd: confirmedStart,
      });
      await loadData();
      await loadOrderDetail(selectedOrderId);
      setMessage({ type: "success", text: "Le rendez-vous a ete confirme." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le rendez-vous n'a pas pu etre confirme." });
    }
  };

  const currentQueueEntry = queue.find((entry) => String(entry.id) === String(selectedOrderId));

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Coordination PMO"
      description="Vue PMO pour la coordination, les affectations et les actions de rendez-vous."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={handleAppointment} disabled={!selectedOrderId} sx={saasHeroActionButtonSx}>
          Enregistrer le rendez-vous
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box sx={{ ...saasDirectoryWrapSx, borderRadius: "44px", px: { xs: 2, md: 2.5 }, py: { xs: 1.2, md: 1.4 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>Afficher {filteredQueue.length} elements</Typography>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} alignItems={{ xs: "stretch", xl: "center" }}>
          <TextField
            label="Rechercher"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ ...saasInputSx, flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "18px", minHeight: 44, backgroundColor: "#fff", boxShadow: "none" } }}
            InputProps={{ startAdornment: <SearchRoundedIcon sx={{ color: "#5f6fff", mr: 1 }} /> }}
          />

          <FormControl sx={{ minWidth: { xs: "100%", xl: 350 } }}>
            <Select value={quickAction} onChange={(event) => setQuickAction(event.target.value)} displayEmpty sx={{ minHeight: 56, borderRadius: "18px", bgcolor: "#fff", fontWeight: 700, boxShadow: "none" }}>
              <MenuItem value="default">Action rapide</MenuItem>
              <MenuItem value="ready">Pret a livrer</MenuItem>
              <MenuItem value="assigned">Avec technicien</MenuItem>
              <MenuItem value="confirmed">Rendez-vous confirme</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ...saasSoftCountSx, minWidth: 102, minHeight: 44, bgcolor: "#f5f7ff" }}>
            <Typography variant="body2" sx={{ color: "#5867ff", fontWeight: 700 }}>{technicians.length} techniciens</Typography>
          </Box>

          <Box sx={{ width: 58, height: 44, borderRadius: "16px", border: "1px solid #dfe6f4", bgcolor: "#fff", display: "grid", placeItems: "center" }}>
            <Badge badgeContent={queue.filter((entry) => entry.status === "ready_for_delivery").length} color="error">
              <IconButton size="small" sx={{ color: "#5a68ff" }}><NotificationsRoundedIcon /></IconButton>
            </Badge>
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} sx={{ mt: 1.25 }}>
          <TextField select label="Commande" value={selectedOrderId} onChange={(event) => setSelectedOrderId(event.target.value)} sx={{ ...saasInputSx, flex: 1 }}>
            {orders.map((order) => (
              <MenuItem key={order.id} value={order.id}>#{order.id} - {order.contactName} - {order.status}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Technicien"
            value={selectedTechnicianId}
            onChange={(event) => {
              setSelectedTechnicianId(event.target.value);
              setAppointmentForm((current) => ({ ...current, technicianId: event.target.value }));
            }}
            sx={{ ...saasInputSx, flex: 1 }}
          >
            {technicians.map((technician) => (
              <MenuItem key={technician.id} value={technician.id}>{technician.first_name} {technician.last_name}</MenuItem>
            ))}
          </TextField>

          <Button variant="contained" onClick={handleAssign} disabled={!selectedOrderId || !selectedTechnicianId} sx={{ minHeight: 46, borderRadius: "14px" }}>
            Affecter le technicien
          </Button>
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.1fr 0.9fr" }, gap: 3 }}>
        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Dossiers PMO</Typography>
              <Typography variant="body2" color="text.secondary">
                Vue structuree avec statut d'affectation, details client et contexte rendez-vous.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.25}>
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Visibles</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{filteredQueue.length}</Typography></Box></Box>
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Selection</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{selectedQueueId ? 1 : 0}</Typography></Box></Box>
            </Stack>
          </Box>

          {filteredQueue.map((entry) => (
            <DirectoryRowCard
              key={`${entry.id}-${entry.appointment_id || "none"}`}
              selected={selectedQueueId === entry.id}
              avatarText={`#${entry.id}`}
              title={entry.contact_name || "Commande inconnue"}
              subtitle={`PMO-${entry.id}`}
              chips={[
                { label: entry.status === "ready_for_delivery" ? "Pret" : entry.status, color: entry.status === "ready_for_delivery" ? "success" : "default" },
                { label: entry.technician_name || "Non affecte", color: "default" },
              ]}
              columns={[
                { label: "Contact", value: entry.whatsapp_number || "-", secondary: entry.location_text || "Aucune localisation" },
                { label: "Rendez-vous", value: entry.confirmed_start ? formatDateTime(entry.confirmed_start) : formatDateTime(entry.option_a_start), secondary: entry.option_b_start ? `Option B ${formatDateTime(entry.option_b_start)}` : "Pas encore de deuxieme option" },
              ]}
              actionLabel={selectedQueueId === entry.id ? "Selectionne" : "Choisir"}
              onSelect={() => {
                setSelectedQueueId(entry.id);
                setSelectedOrderId(String(entry.id));
                if (entry.technician_name) {
                  const match = technicians.find((tech) => `${tech.first_name} ${tech.last_name}` === entry.technician_name);
                  setSelectedTechnicianId(match ? String(match.id) : "");
                }
              }}
            />
          ))}
        </Box>

        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 }, alignContent: "start" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Espace PMO</Typography>
            <Typography variant="body2" color="text.secondary">Affectation, propositions de rendez-vous et suivi final PMO.</Typography>
          </Box>

          {!selectedOrder ? (
            <Typography variant="body2" color="text.secondary">Choisissez un dossier PMO a gauche pour le modifier.</Typography>
          ) : (
            <>
              <Box sx={{ ...saasSoftCountSx, minWidth: 130 }}>
                <Typography variant="caption" sx={{ color: "#7d89a2" }}>Statut actuel</Typography>
                <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>{selectedOrder.status}</Typography>
              </Box>
              <TextField
                select
                label="Technicien"
                value={selectedTechnicianId}
                onChange={(event) => {
                  setSelectedTechnicianId(event.target.value);
                  setAppointmentForm((current) => ({ ...current, technicianId: event.target.value }));
                }}
                sx={saasInputSx}
              >
                {technicians.map((technician) => (
                  <MenuItem key={technician.id} value={technician.id}>{technician.first_name} {technician.last_name}</MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={handleAssign} disabled={!selectedOrderId || !selectedTechnicianId} sx={{ borderRadius: "14px" }}>
                Affecter maintenant
              </Button>

              <Box sx={{ border: "1px solid #e1e7f2", borderRadius: "16px", p: 1.5, bgcolor: "#fdfefe" }}>
                <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800, mb: 1 }}>Statut du rendez-vous</Typography>
                <Typography variant="body2" color="text.secondary">Option A: {currentQueueEntry?.option_a_start ? `${formatDateTime(currentQueueEntry.option_a_start)} - ${formatDateTime(currentQueueEntry.option_a_end)}` : "-"}</Typography>
                <Typography variant="body2" color="text.secondary">Option B: {currentQueueEntry?.option_b_start ? `${formatDateTime(currentQueueEntry.option_b_start)} - ${formatDateTime(currentQueueEntry.option_b_end)}` : "-"}</Typography>
                <Typography variant="body2" color="text.secondary">Confirme: {currentQueueEntry?.confirmed_start ? `${formatDateTime(currentQueueEntry.confirmed_start)} - ${formatDateTime(currentQueueEntry.confirmed_end)}` : "-"}</Typography>
                <Typography variant="body2" color="text.secondary">Statut: {currentQueueEntry?.appointment_status || "Aucune proposition"}</Typography>
                {currentQueueEntry?.option_a_start ? (
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ mt: 1.25 }}>
                    <Button variant="outlined" onClick={() => handleConfirm(currentQueueEntry.option_a_start)} sx={{ borderRadius: "14px" }}>
                      Confirmer option A
                    </Button>
                    {currentQueueEntry?.option_b_start ? (
                      <Button variant="outlined" onClick={() => handleConfirm(currentQueueEntry.option_b_start)} sx={{ borderRadius: "14px" }}>
                        Confirmer option B
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}
              </Box>

              <Box sx={{ border: "1px solid #e1e7f2", borderRadius: "16px", p: 1.5, bgcolor: "#fdfefe", display: "grid", gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800 }}>Modifier les propositions</Typography>
                <TextField type="datetime-local" label="Debut option A" InputLabelProps={{ shrink: true }} value={appointmentForm.optionAStart} onChange={(e) => setAppointmentForm((c) => ({ ...c, optionAStart: e.target.value }))} sx={saasInputSx} />
                <TextField type="datetime-local" label="Fin option A" InputLabelProps={{ shrink: true }} value={appointmentForm.optionAEnd} onChange={(e) => setAppointmentForm((c) => ({ ...c, optionAEnd: e.target.value }))} sx={saasInputSx} />
                <TextField type="datetime-local" label="Debut option B" InputLabelProps={{ shrink: true }} value={appointmentForm.optionBStart} onChange={(e) => setAppointmentForm((c) => ({ ...c, optionBStart: e.target.value }))} sx={saasInputSx} />
                <TextField type="datetime-local" label="Fin option B" InputLabelProps={{ shrink: true }} value={appointmentForm.optionBEnd} onChange={(e) => setAppointmentForm((c) => ({ ...c, optionBEnd: e.target.value }))} sx={saasInputSx} />
                <Button variant="contained" onClick={handleAppointment} disabled={!selectedOrderId || !appointmentForm.optionAStart} sx={{ borderRadius: "14px" }}>
                  Enregistrer les propositions
                </Button>
              </Box>

              <Box sx={{ display: "grid", gap: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800 }}>Historique de commande</Typography>
                {(selectedOrder.history || []).map((entry) => (
                  <Box key={entry.id} sx={{ border: "1px solid #e1e7f2", borderRadius: "16px", p: 1.25, bgcolor: "#fdfefe" }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#25324b" }}>{entry.oldStatus || "debut"} -> {entry.newStatus}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>{entry.note || "Aucune note"}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Calendrier central des rendez-vous</Typography>
            <Typography variant="body2" color="text.secondary">
              Toutes les propositions actives et les rendez-vous confirmes dans la vue PMO centrale.
            </Typography>
          </Box>
          <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Entrees</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{calendarEntries.length}</Typography></Box></Box>
        </Box>

        {calendarEntries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucune proposition de rendez-vous pour le moment.</Typography>
        ) : (
          calendarEntries.map((entry) => (
            <DirectoryRowCard
              key={`calendar-${entry.id}`}
              avatarText={`#${entry.order_id}`}
              title={entry.contact_name || "Commande inconnue"}
              subtitle={`APT-${entry.id}`}
              chips={[
                { label: entry.status || "brouillon", color: entry.status === "confirmed" ? "success" : "default" },
                { label: entry.technician_name || "Non affecte", color: "default" },
              ]}
              columns={[
                {
                  label: "Option A",
                  value: entry.option_a_start ? `${formatDateTime(entry.option_a_start)} - ${formatDateTime(entry.option_a_end)}` : "-",
                  secondary: entry.whatsapp_number || "-",
                },
                {
                  label: "Option B / Confirmation",
                  value: entry.confirmed_start
                    ? `${formatDateTime(entry.confirmed_start)} - ${formatDateTime(entry.confirmed_end)}`
                    : entry.option_b_start
                      ? `${formatDateTime(entry.option_b_start)} - ${formatDateTime(entry.option_b_end)}`
                      : "-",
                  secondary: entry.confirmed_start ? "Confirme" : "Proposition",
                },
              ]}
              actionLabel={selectedOrderId === String(entry.order_id) ? "Selectionne" : "Ouvrir"}
              onSelect={() => {
                setSelectedQueueId(entry.order_id);
                setSelectedOrderId(String(entry.order_id));
              }}
            />
          ))
        )}
      </Box>
    </PageFrame>
  );
}
